// --- FINAL, UPDATED FILE: packages/agent-kit/src/hooks/projects/useManageProject.ts ---

import { useState, useCallback } from 'react';
import { adminProjectService, isApiError } from '@dopomogai/api-client';
import type { Project, UUID } from '@dopomogai/types'; // Removed RevertPayload as it's not used here
import { AgentKitError } from '../../types';

/**
 * A hook for providing functions to perform administrative actions on Projects.
 * This is a "mutation" hook and does not fetch data itself. It provides the actions
 * to create, update, and delete projects.
 */
export const useManageProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AgentKitError | null>(null);

  /**
   * Creates a new project on the server.
   * @param projectData The data for the new project.
   * @returns The newly created Project object, or null on failure.
   */
  const createProject = useCallback(
    async (projectData: Omit<Project, 'id' | 'version' | 'created_at' | 'updated_at'>): Promise<Project | null> => {
      setIsLoading(true);
      setError(null);
      const res = await adminProjectService.createProject(projectData);
      if (isApiError(res)) {
        setError(new AgentKitError('Failed to create project', res));
        setIsLoading(false);
        return null;
      }
      setIsLoading(false);
      return res;
    }, 
  []);

  /**
   * Updates an existing project on the server.
   * @param projectId The UUID of the project to update.
   * @param projectData The full project object with updated fields.
   * @returns The updated Project object, or null on failure.
   */
  const updateProject = useCallback(async (projectId: UUID, projectData: Project): Promise<Project | null> => {
    setIsLoading(true);
    setError(null);
    const res = await adminProjectService.updateProject(projectId, projectData);
    if (isApiError(res)) {
      setError(new AgentKitError('Failed to update project', res));
      setIsLoading(false);
      return null;
    }
    setIsLoading(false);
    return res;
  }, []);

  /**
   * Permanently deletes a project and all its associated data.
   * --- FIX: This function is now more robust. ---
   * @param projectId The UUID of the project to delete.
   * @returns `true` on success, `false` on failure.
   */
  const deleteProject = useCallback(async (projectId: UUID): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    const res = await adminProjectService.deleteProject(projectId);
    
    // The delete endpoint returns `void` on success, so we check for an error.
    if (isApiError(res)) {
      setError(new AgentKitError('Failed to delete project', res));
      setIsLoading(false);
      return false; // Indicate failure
    }
    
    setIsLoading(false);
    return true; // Indicate success
  }, []);
  
  return { 
    createProject, 
    updateProject, 
    deleteProject, 
    isLoading, 
    error 
  };
};